import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

const DynamicRegister = ({ onBack }) => {
  const navigate = useNavigate();
  const { type: userType } = useParams();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [businessProfileImage, setBusinessProfileImage] = useState(null);
  const [businessImages, setBusinessImages] = useState([]);
  const [, setCompressedImage] = useState(null);

  // Common fields for both user types
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirm_password: '',
    first_name: '',
    last_name: '',
    phone: '',
    user_type: userType || 'customer'
  });

  // Customer specific fields
  const [customerData, setCustomerData] = useState({
    preferred_language: 'en',
    marketing_consent: false
  });

  // Business owner specific fields
  const [businessData, setBusinessData] = useState({
    business_name: '',
    business_type: 'salon',
    business_address: '',
    business_phone: '',
    business_email: '',
    business_currency_code: 'USD',
    business_latitude: '',
    business_longitude: ''
  });

  const currencyOptions = [
    { value: 'PKR', label: 'Pakistan Rupees', symbol: '₨' },
    { value: 'USD', label: 'US Dollar', symbol: '$' },
    { value: 'EUR', label: 'Euro', symbol: '€' },
    { value: 'CHF', label: 'Swiss Franc', symbol: 'CHF' },
    { value: 'GBP', label: 'Pound', symbol: '£' }
  ];

  const useMyLocation = async () => {
    if (!navigator?.geolocation) {
      toast.error('Geolocation is not supported in this browser');
      return;
    }

    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos?.coords?.latitude;
        const lng = pos?.coords?.longitude;
        setBusinessData((prev) => ({
          ...prev,
          business_latitude: Number.isFinite(lat) ? String(lat) : '',
          business_longitude: Number.isFinite(lng) ? String(lng) : ''
        }));
        setGeoLoading(false);
      },
      (err) => {
        toast.error(err?.message || 'Failed to get location');
        setGeoLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validatePhone = (phone) => {
      return /^\+[1-9]\d{7,14}$/.test(phone);
    };

    if (formData.phone && !validatePhone(formData.phone)) {
      toast.error('Enter valid phone number with country code');
      return;
    }

    if (formData.password !== formData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const formDataObj = new FormData();
      
      formDataObj.append('email', formData.email);
      formDataObj.append('password', formData.password);
      formDataObj.append('first_name', formData.first_name);
      formDataObj.append('last_name', formData.last_name);
      formDataObj.append('phone', formData.phone);
      formDataObj.append('user_type', userType);

      if (profileImage) {
        formDataObj.append('profile_image', profileImage);
      }

      if (userType === 'customer') {
        formDataObj.append('preferred_language', customerData.preferred_language);
        formDataObj.append('marketing_consent', customerData.marketing_consent);
      } else {
        const hasLat = String(businessData.business_latitude || '').trim() !== '';
        const hasLng = String(businessData.business_longitude || '').trim() !== '';
        if (hasLat !== hasLng) {
          toast.error('Please provide both latitude and longitude (or leave both empty)');
          setLoading(false);
          return;
        }

        formDataObj.append('business_name', businessData.business_name);
        formDataObj.append('business_type', businessData.business_type);
        formDataObj.append('business_address', businessData.business_address);
        formDataObj.append('business_phone', businessData.business_phone);
        formDataObj.append('business_email', businessData.business_email);
        formDataObj.append('business_currency_code', businessData.business_currency_code);
        if (hasLat && hasLng) {
          formDataObj.append('business_latitude', businessData.business_latitude);
          formDataObj.append('business_longitude', businessData.business_longitude);
        }

        if (businessProfileImage) {
          formDataObj.append('business_profile_image', businessProfileImage);
        }

        businessImages.forEach((file) => {
          formDataObj.append('business_images', file);
        });
      }

      const response = await register(formDataObj);

      if (response && response.requires_approval) {
        navigate('/registration-pending');
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Registration failed:', error);
      toast.error(error.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const businessTypes = [
    { value: 'salon', label: 'Salon / Spa', icon: '💇' },
    { value: 'clinic', label: 'Clinic / Medical', icon: '🏥' },
    { value: 'gym', label: 'Gym / Fitness', icon: '💪' },
    { value: 'ride', label: 'Ride / Transportation', icon: '🚗' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-2xl mx-auto">
        <button
          onClick={() => onBack ? onBack() : navigate('/choose-type')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to selection
        </button>

        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900">
              {userType === 'customer' ? 'Create Customer Account' : 'Register Your Business'}
            </h1>
            <p className="text-gray-600 mt-2">
              {userType === 'customer'
                ? 'Join BookFlow to discover and book amazing services'
                : 'Start accepting bookings and grow your business'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Information Section */}
            <div className="border-b pb-4">
              <h2 className="text-lg font-semibold mb-4">Personal Information</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">First Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    className="w-full input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Last Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    className="w-full input"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium mb-1">Email *</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full input"
                />
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium mb-1">Phone</label>
                {/* <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full input"
                /> */}
                <PhoneInput
                  country={'pk'}
                  value={formData.phone}
                  onChange={(phone) =>
                    setFormData({
                      ...formData,
                      phone: phone.startsWith('+') ? phone : '+' + phone
                    })
                  }
                  inputClass="!w-full !h-10 !text-sm"
                  containerClass="mt-1"
                  enableSearch
                  countryCodeEditable={false}
                />
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium mb-1">Profile Image (Optional)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) {
                      setProfileImage(null);
                      setCompressedImage(null);
                      return;
                    }
                    setCompressedImage(file);
                    setProfileImage(file);
                  }}
                  className="w-full input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Password *</label>
                  <input
                    type="password"
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Confirm Password *</label>
                  <input
                    type="password"
                    required
                    value={formData.confirm_password}
                    onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                    className="w-full input"
                  />
                </div>
              </div>
            </div>

            {/* Customer Specific Fields */}
            {userType === 'customer' && (
              <div>
                <h2 className="text-lg font-semibold mb-4">Preferences</h2>
                <div>
                  <label className="block text-sm font-medium mb-1">Preferred Language</label>
                  <select
                    value={customerData.preferred_language}
                    onChange={(e) => setCustomerData({ ...customerData, preferred_language: e.target.value })}
                    className="w-full input"
                  >
                    <option value="en">English</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                    <option value="de">German</option>
                  </select>
                </div>

                <div className="mt-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={customerData.marketing_consent}
                      onChange={(e) => setCustomerData({ ...customerData, marketing_consent: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">
                      I agree to receive marketing communications and special offers
                    </span>
                  </label>
                </div>
              </div>
            )}

            {/* Business Owner Specific Fields */}
            {userType === 'business_owner' && (
              <div>
                <h2 className="text-lg font-semibold mb-4">Business Information</h2>

                <div>
                  <label className="block text-sm font-medium mb-1">Business Name *</label>
                  <input
                    type="text"
                    required
                    value={businessData.business_name}
                    onChange={(e) => setBusinessData({ ...businessData, business_name: e.target.value })}
                    className="w-full input"
                    placeholder="Your Business Name"
                  />
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium mb-1">Business Type *</label>
                  <div className="grid grid-cols-2 gap-3">
                    {businessTypes.map((type) => (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => setBusinessData({ ...businessData, business_type: type.value })}
                        className={`p-3 border rounded-lg text-center transition-all ${businessData.business_type === type.value
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-300 hover:border-primary-300'
                          }`}
                      >
                        <span className="text-2xl block">{type.icon}</span>
                        <span className="text-sm">{type.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium mb-1">Business Email *</label>
                  <input
                    type="email"
                    required
                    value={businessData.business_email}
                    onChange={(e) => setBusinessData({ ...businessData, business_email: e.target.value })}
                    className="w-full input"
                    placeholder="contact@yourbusiness.com"
                  />
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium mb-1">Business Phone *</label>
                  {/* <input
                    type="tel"
                    required
                    value={businessData.business_phone}
                    onChange={(e) => setBusinessData({ ...businessData, business_phone: e.target.value })}
                    className="w-full input"
                    placeholder="+1 234 567 8900"
                  /> */}
                  <PhoneInput
                    country={'pk'}
                    value={businessData.business_phone}
                    onChange={(phone) =>
                      setBusinessData({
                        ...businessData,
                        business_phone: phone.startsWith('+') ? phone : '+' + phone
                      })
                    }
                    inputClass="!w-full !h-10 !text-sm"
                    containerClass="mt-1"
                    enableSearch
                    countryCodeEditable={false}
                  />
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium mb-1">Business Address *</label>
                  <textarea
                    required
                    rows="3"
                    value={businessData.business_address}
                    onChange={(e) => setBusinessData({ ...businessData, business_address: e.target.value })}
                    className="w-full input"
                    placeholder="Full business address"
                  />
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium mb-1">Business Currency</label>
                  <select
                    value={businessData.business_currency_code}
                    onChange={(e) => setBusinessData({ ...businessData, business_currency_code: e.target.value })}
                    className="w-full input"
                  >
                    {currencyOptions.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label} ({c.symbol})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mt-4">
                  <div className="flex items-center justify-between gap-3">
                    <label className="block text-sm font-medium mb-1">Business Location (Optional)</label>
                    <button
                      type="button"
                      onClick={useMyLocation}
                      disabled={geoLoading}
                      className="text-sm text-primary-600 hover:text-primary-700 disabled:opacity-60"
                    >
                      {geoLoading ? 'Getting location...' : 'Use my location'}
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Latitude</label>
                      <input
                        type="number"
                        inputMode="decimal"
                        step="0.0000001"
                        value={businessData.business_latitude}
                        onChange={(e) => setBusinessData({ ...businessData, business_latitude: e.target.value })}
                        className="w-full input"
                        placeholder="e.g. 24.8607"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Longitude</label>
                      <input
                        type="number"
                        inputMode="decimal"
                        step="0.0000001"
                        value={businessData.business_longitude}
                        onChange={(e) => setBusinessData({ ...businessData, business_longitude: e.target.value })}
                        className="w-full input"
                        placeholder="e.g. 67.0011"
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium mb-1">Business Profile Image (Optional)</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) {
                        setBusinessProfileImage(null);
                      setCompressedImage(null);
                        return;
                      }
                    setCompressedImage(file);
                      setBusinessProfileImage(file);
                    }}
                    className="w-full input"
                  />
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium mb-1">Business Images (Optional, up to 5)</label>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []).slice(0, 5);
                      setBusinessImages(files);
                    }}
                    className="w-full input"
                  />
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary py-3 disabled:opacity-50"
            >
              {loading
                ? 'Processing...'
                : userType === 'business_owner'
                  ? 'Submit for Admin Approval'
                  : 'Create Account'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DynamicRegister;
